import { useEffect, useState } from 'react';
import { useOrganization } from '@/auth/AuthContext';
import { countries } from '@/data/Countries';
import { locationService, type IpLocation } from '@/service/LocationService';

export const DEFAULT_LOCATION: IpLocation = { country: 'CH', city: 'Zurich' };
export type LocationMode = 'organization' | 'public';

const normalize = (country?: string | null, city?: string | null): IpLocation | null => {
    const code = country?.trim().toUpperCase() ?? '';
    return Object.prototype.hasOwnProperty.call(countries, code)
        ? { country: code, city: city?.trim() ?? '' } : null;
};

/** Defaults are immediately usable; organization settings take priority when available. */
export const useLocationDefaults = (mode: LocationMode = 'organization') => {
    const organization = useOrganization();
    const organizationLocation = mode === 'organization'
        ? normalize(organization?.country, organization?.city) : null;
    const hasOrganizationLocation = organizationLocation !== null;
    const [detected, setDetected] = useState<IpLocation | null>(null);
    const [finished, setFinished] = useState(false);

    useEffect(() => {
        if (hasOrganizationLocation || finished) return;
        const controller = new AbortController();
        let active = true;
        const finish = (location: IpLocation | null) => {
            if (!active) return;
            active = false;
            clearTimeout(timeout);
            controller.abort();
            setDetected(location);
            setFinished(true);
        };
        const timeout = setTimeout(() => finish(null), 5000);
        locationService.current(controller.signal)
            .then(value => finish(normalize(value.country, value.city)))
            .catch(() => finish(null));
        return () => { active = false; clearTimeout(timeout); controller.abort(); };
    }, [hasOrganizationLocation, finished]);

    return {
        location: organizationLocation ?? detected ?? DEFAULT_LOCATION,
        ready: hasOrganizationLocation || finished,
        source: hasOrganizationLocation ? 'organization' : detected ? 'detected' : finished ? 'fallback' : null,
    };
};
